import React from 'react';
import { Heart } from 'lucide-react';

interface HeroBabySectionProps {
  content: any;
  language: 'en' | 'mr';
}

export const HeroBabySection: React.FC<HeroBabySectionProps> = ({ content, language }) => {
  const parentNames = language === 'mr' ? content.baby.parentNamesMr : content.baby.parentNames;
  const babyName = language === 'mr' ? content.baby.nameMr : content.baby.name;
  const message = language === 'mr' ? content.event.messageMr : content.event.message;

  return (
    <section className="relative min-h-screen flex items-center justify-center px-4 py-20 overflow-hidden">
      {/* Floating Balloons */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-10 left-10 w-20 h-24 bg-yellow-300 rounded-full opacity-20 animate-bounce" style={{ animationDelay: '0s' }} />
        <div className="absolute top-20 right-20 w-16 h-20 bg-pink-300 rounded-full opacity-20 animate-bounce" style={{ animationDelay: '0.2s' }} />
        <div className="absolute bottom-20 left-1/4 w-24 h-28 bg-blue-300 rounded-full opacity-20 animate-bounce" style={{ animationDelay: '0.4s' }} />
      </div>

      <div className="relative z-10 max-w-4xl w-full grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        {/* Baby Photo */}
        <div className="flex justify-center">
          <div className="relative w-full aspect-square max-w-sm">
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-300 to-pink-300 rounded-3xl blur-2xl opacity-50" />
            <img
              src={content.baby.photo}
              alt="Baby"
              className="relative w-full h-full object-cover rounded-3xl shadow-2xl border-4 border-yellow-300"
            />
            <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-yellow-300 to-pink-300 rounded-full p-6 shadow-lg transform hover:scale-110 transition-transform">
              <Heart size={32} className="text-white fill-white" />
            </div>
          </div>
        </div>

        {/* Text Content */}
        <div className="flex flex-col gap-6">
          <div className="space-y-4">
            <p className="text-lg text-yellow-600 font-bold uppercase tracking-widest">
              {language === 'mr' ? '👶 बेबी शावर' : '👶 Baby Shower'}
            </p>
            <h1 className="text-6xl md:text-7xl font-black bg-gradient-to-r from-yellow-500 to-pink-500 bg-clip-text text-transparent">
              {babyName}
            </h1>
            <p className="text-2xl font-bold text-gray-700">
              {language === 'mr' ? 'येत आहे!' : 'is on the way!'}
            </p>
            <p className="text-lg text-gray-600 font-semibold">
              {parentNames}
            </p>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed">
            {message}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 pt-6">
            <button className="px-8 py-4 bg-gradient-to-r from-yellow-400 to-orange-400 text-white font-bold rounded-full hover:shadow-lg hover:shadow-yellow-400/50 transition-all transform hover:scale-110">
              {language === 'mr' ? '🎉 खेळ खेळा' : '🎉 Play Games'}
            </button>
            <button className="px-8 py-4 bg-gradient-to-r from-pink-400 to-rose-400 text-white font-bold rounded-full hover:shadow-lg hover:shadow-pink-400/50 transition-all transform hover:scale-110">
              {language === 'mr' ? '🎁 RSVP करा' : '🎁 RSVP'}
            </button>
          </div>

          {/* Info Badges */}
          <div className="flex gap-3 pt-4 flex-wrap">
            <div className="px-4 py-2 bg-yellow-100 border-2 border-yellow-400 rounded-full text-sm font-semibold text-yellow-700">
              📅 {language === 'mr' ? content.event.dateMr : content.event.dateStr}
            </div>
            <div className="px-4 py-2 bg-pink-100 border-2 border-pink-400 rounded-full text-sm font-semibold text-pink-700">
              🕐 {content.event.time}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
